import { useMemo } from 'react'
import { convertErrorToken } from './convertErrorToken'
import {
  TURNSTILE_PROXY_ERROR_LIST,
  TURNSTILE_STATUSES,
  TURNSTILE_TOOLTIP_MESSAGES,
} from '../consts'
import { getter } from '../../../utils/proxyGetter'

/**
 * Factory function that creates a Proxy object for managing tooltip titles based on turnstile statuses.
 * This Proxy dynamically fetches the appropriate message for different statuses, enhancing the user experience
 * by providing contextual feedback.
 */
const TOOLTIP_TITLE = (token) =>
  new Proxy(
    {
      [TURNSTILE_STATUSES.UNSUPPORTED]: {
        message: TURNSTILE_TOOLTIP_MESSAGES.UNSUPPORTED,
      },
      [TURNSTILE_STATUSES.ERROR]:
        TURNSTILE_PROXY_ERROR_LIST[
          convertErrorToken(token, TURNSTILE_PROXY_ERROR_LIST)
        ],
      default: {
        message: TURNSTILE_TOOLTIP_MESSAGES.NEED_VERIFICATION,
      },
    },
    { get: getter },
  )

/**
 * Defines a list of error-related turnstile statuses that should trigger a tooltip.
 * This list is crucial for determining when to display tooltips to the user, focusing on critical
 * statuses that indicate potential issues or require attention.
 */
const ERROR_TURNSTILE_TOOLTIP_STATUSES = [
  TURNSTILE_STATUSES.EXPIRED,
  TURNSTILE_STATUSES.TIMEOUT,
  TURNSTILE_STATUSES.BEFORE_INTERACTIVE,
]

/**
 * Custom React hook that evaluates whether a tooltip should be displayed and sets its title based on the current turnstile status.
 * This hook plays a vital role in improving the user interface by offering timely and relevant feedback to the user.
 *
 * @param {Object} turnstileStatus - The current status object of the turnstile, expected to contain properties like `status` and `token`.
 * @returns {{open: Boolean, title: String}} Returns an object with a boolean flag indicating whether the tooltip should be shown and the title content for the tooltip.
 */
export const useTooltipConfig = (turnstileStatus) => {
  const open = useMemo(
    () => ERROR_TURNSTILE_TOOLTIP_STATUSES.includes(turnstileStatus?.status),
    [turnstileStatus?.status],
  )

  const statusConfig = useMemo(
    () => TOOLTIP_TITLE(turnstileStatus?.token || '')[turnstileStatus?.status],
    [turnstileStatus],
  )

  return { open, statusConfig }
}
